import type { QuestionDefinition } from "@reno-estimator/core";

/**
 * Questions asked for every project type, regardless of which room/space
 * is being renovated. Project-specific question sets (see ./kitchen.ts,
 * ./bathroom.ts, etc.) are appended after these.
 */
export const commonQuestions: QuestionDefinition[] = [
  {
    id: "location",
    projectTypes: "all",
    inputType: "location",
    prompt: "Where is the project located?",
    helpText: "Used to apply regional pricing for your area.",
    required: true,
    order: 10
  },
  {
    id: "room_size",
    projectTypes: "all",
    inputType: "dimension",
    prompt: "Approximately how large is the space?",
    helpText: "A rough square footage is fine.",
    required: false,
    min: 0,
    max: 10000,
    order: 20
  },
  {
    id: "scope",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "What best describes the scope of this project?",
    required: true,
    options: [
      { id: "cosmetic_refresh", label: "Cosmetic refresh", description: "Paint, fixtures, surfaces - no layout changes." },
      { id: "mid_range_remodel", label: "Mid-range remodel", description: "Some layout or system changes, updated finishes." },
      { id: "full_gut_renovation", label: "Full gut renovation", description: "Down to studs, full layout freedom." },
      { id: "custom_high_end", label: "Custom / high-end", description: "Bespoke design, high-end materials and finishes." }
    ],
    order: 30
  },
  {
    id: "finish_level",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "What finish level are you aiming for?",
    required: true,
    options: [
      { id: "budget", label: "Budget" },
      { id: "standard", label: "Standard" },
      { id: "premium", label: "Premium" },
      { id: "luxury", label: "Luxury" }
    ],
    order: 40
  },
  {
    id: "plumbing_changes",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "Will this project involve moving or adding plumbing?",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 50
  },
  {
    id: "electrical_changes",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "Will this project involve moving or adding electrical?",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 60
  },
  {
    id: "structural_changes",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "Will any walls or structural elements be changed (e.g. removing a wall)?",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 70
  },
  {
    id: "demolition_required",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "Is demolition of the existing space required?",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 80
  },
  {
    id: "accessibility_required",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "Does this project need to accommodate accessibility or mobility needs?",
    helpText: "For example: a curbless shower, widened doorways, grab bars, lever hardware, or lowered counters.",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure yet" }
    ],
    order: 85
  },
  {
    id: "accessibility_notes",
    projectTypes: "all",
    inputType: "text",
    prompt: "Anything specific we should know about accessibility needs?",
    required: false,
    displayIf: [{ questionId: "accessibility_required", equalsAnyOf: ["yes", "unknown"] }],
    order: 86
  },
  {
    id: "needs_design_services",
    projectTypes: "all",
    inputType: "boolean",
    prompt: "Do you want design services included (e.g. an interior designer or drafter)?",
    required: true,
    order: 90
  },
  {
    id: "permit_likely_required",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "Do you know if a permit is required for this project?",
    helpText: "If unsure, the engine will flag this as an assumption to confirm with your municipality.",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 100
  },
  {
    id: "timeline_window",
    projectTypes: "all",
    inputType: "single_select",
    prompt: "When would you like to start?",
    required: false,
    options: [
      { id: "asap", label: "As soon as possible" },
      { id: "1_3_months", label: "1-3 months" },
      { id: "3_6_months", label: "3-6 months" },
      { id: "6_12_months", label: "6-12 months" },
      { id: "flexible", label: "I'm flexible" }
    ],
    order: 110
  }
];
